import { lazy, Suspense, useEffect, useRef, useState, useCallback } from 'react';
import { motion as Motion } from 'framer-motion';
import { Layers3, Monitor, Smartphone, Cloud, ArrowRight, CheckCircle2 } from 'lucide-react';

const TechStack3D = lazy(() => import('./TechStack3D'));

function useTilt() {
  const ref = useRef(null);
  const [tiltStyle, setTiltStyle] = useState({
    transform: 'perspective(800px) rotateX(0deg) rotateY(0deg)',
    transition: 'transform 0.4s ease',
  });

  const handleMouseMove = useCallback((e) => {
    const card = ref.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    const rotateX = dy * -15;
    const rotateY = dx * 15;
    setTiltStyle({
      transform: `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      transition: 'transform 0.1s ease',
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTiltStyle({
      transform: 'perspective(800px) rotateX(0deg) rotateY(0deg)',
      transition: 'transform 0.4s ease',
    });
  }, []);

  return { ref, tiltStyle, handleMouseMove, handleMouseLeave };
}

const EASE = [0.22, 1, 0.36, 1];

function useLazyMount(threshold = 0.1) {
  const [mounted, setMounted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted, threshold]);

  return { ref, mounted };
}

function TechStack3DLazy() {
  const { ref, mounted } = useLazyMount(0.1);
  return (
    <div ref={ref} style={{ minHeight: 320 }}>
      {mounted && (
        <Suspense fallback={null}>
          <TechStack3D />
        </Suspense>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION 1 — CINEMATIC HERO STATEMENT
   ───────────────────────────────────────────────────────────────────────────── */
function HeroStatement({ reducedMotion }) {
  return (
    <div className="cap-hero">
      <Motion.h2
        className="cap-hero__headline"
        initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 32 }}
        whileInView={reducedMotion ? undefined : {
          opacity: 1,
          y: 0,
          transition: { type: 'spring', damping: 25, stiffness: 100, duration: 1.1 }
        }}
        viewport={{ once: true }}
      >
        We engineer systems that think.
      </Motion.h2>
      
      <Motion.p
        className="cap-hero__subtext"
        initial={reducedMotion ? { opacity: 1 } : { opacity: 0 }}
        whileInView={reducedMotion ? undefined : { opacity: 0.5, transition: { delay: 0.6, duration: 0.8 } }}
        viewport={{ once: true }}
      >
        &gt; FULL-STACK · MOBILE · CLOUD · IMMERSIVE
      </Motion.p>

      <div className="cap-hero__line-wrap">
        <Motion.div
          className="cap-hero__line"
          initial={reducedMotion ? { scaleX: 1 } : { scaleX: 0 }}
          whileInView={reducedMotion ? undefined : { scaleX: 1, transition: { duration: 1.4, ease: EASE, delay: 0.2 } }}
          viewport={{ once: true }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION 2 — DOMAIN CLUSTER PANELS
   ───────────────────────────────────────────────────────────────────────────── */
const DOMAIN_DATA = [
  { id: 'fe', title: 'Product Engineering', outcome: 'From schema to deployment in under 4 weeks', icon: Layers3 },
  { id: 'im', title: 'Immersive Interface', outcome: '3D WebGL experiences that convert on mobile', icon: Monitor },
  { id: 'mo', title: 'Mobile Systems', outcome: 'Cross-platform Flutter apps with AR capability', icon: Smartphone },
  { id: 'cl', title: 'Cloud & DevOps', outcome: 'Automated CI/CD pipelines with zero-downtime', icon: Cloud },
];

function DomainCard({ domain, pillar, index, reducedMotion }) {
  const Icon = domain.icon;
  const { ref, tiltStyle, handleMouseMove, handleMouseLeave } = useTilt();

  return (
    <Motion.div
      ref={reducedMotion ? undefined : ref}
      className="glass-card cap-domain-card"
      initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 40 }}
      whileInView={reducedMotion ? undefined : {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: index * 0.08, ease: EASE }
      }}
      viewport={{ once: true, amount: 0.2 }}
      style={reducedMotion ? undefined : tiltStyle}
      onMouseMove={reducedMotion ? undefined : handleMouseMove}
      onMouseLeave={reducedMotion ? undefined : handleMouseLeave}
    >
      <div className="cap-domain-card__signal-wrap">
        <span className="cap-domain-card__signal-label">SIGNAL</span>
        <Motion.div
          className="cap-domain-card__signal-bar"
          initial={{ height: '0%' }}
          whileInView={{ height: '100%' }}
          transition={{ duration: 1.2, delay: index * 0.08 + 0.3, ease: 'easeOut' }}
          viewport={{ once: true }}
        />
      </div>

      <div className="cap-domain-card__inner">
        <div className="cap-domain-card__badge">
          <Icon size={20} className="cap-domain-card__badge-icon" />
        </div>
        <h3 className="cap-domain-card__title">{domain.title}</h3>
        <p className="cap-domain-card__outcome">{domain.outcome}</p>
        <div className="cap-domain-card__tags">
          {pillar?.skills?.slice(0, 8).map(skill => (
            <span key={skill} className="hs-tag">{skill}</span>
          ))}
        </div>
      </div>
      <div className="cap-domain-card__inner-glow" />
    </Motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION 4 — PROCESS FLOW
   ───────────────────────────────────────────────────────────────────────────── */
function ProcessFlow({ processSteps, reducedMotion }) {
  return (
    <div className="cap-process">
      <div className="cap-process__headline-wrap">
        <span className="eyebrow">The Studio Pipeline</span>
        <h3 className="section-title">Execution mapped to minutes.</h3>
      </div>

      <div className="cap-process__track">
        {processSteps.map((step, idx) => (
          <Motion.div
            key={step.name}
            className="cap-process__node-wrap"
            initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
            whileInView={reducedMotion ? undefined : { 
              opacity: 1, 
              y: 0, 
              transition: { duration: 0.6, delay: idx * 0.1, ease: EASE } 
            }}
            viewport={{ once: true }}
          >
            {/* Connector Line (except for last node) */}
            {idx < processSteps.length - 1 && (
              <div className="cap-process__connector">
                <svg className="cap-process__connector-svg" preserveAspectRatio="none">
                  <line x1="0" y1="50%" x2="100%" y2="50%" className="cap-process__connector-line" />
                  <Motion.line 
                    x1="0" y1="50%" x2="100%" y2="50%" 
                    className="cap-process__connector-pulse"
                    animate={{ strokeDashoffset: [200, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  />
                </svg>
              </div>
            )}

            <div className={`cap-process__node glass-card`}>
              <div className="cap-process__node-head">
                <span className="cap-process__node-idx">{String(idx + 1).padStart(2, '0')}</span>
                <div className="cap-process__node-indicator" />
              </div>
              <h4>{step.name}</h4>
              <p>{step.outcome}</p>
            </div>
          </Motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   CAPABILITIES SECTION MAIN
   ───────────────────────────────────────────────────────────────────────────── */
export default function CapabilitiesSection({ content, reducedMotion }) {
  const pillars = content.capabilityPillars || [];
  const processSteps = content.process?.steps || [];

  return (
    <section id="capabilities" className="story-section capabilities-section">
      <div className="section-shell">
        
        {/* SECTION 1 */}
        <HeroStatement reducedMotion={reducedMotion} />

        {/* SECTION 2 */}
        <div className="cap-grid">
          {DOMAIN_DATA.map((domain, idx) => (
            <DomainCard 
              key={domain.id} 
              domain={domain} 
              pillar={pillars[idx]} 
              index={idx}
              reducedMotion={reducedMotion} 
            />
          ))}
        </div>

        {/* SECTION 3 */}
        <div className="cap-viz-wrap">
          <div className="cap-viz-header">
             <span className="eyebrow">The Stack</span>
             <h3>A unified engineering architecture.</h3>
          </div>
          <TechStack3DLazy />
        </div>

        {/* SECTION 4 */}
        <ProcessFlow processSteps={processSteps} reducedMotion={reducedMotion} />

      </div>
    </section>
  );
}
